/** Ops Console integrations API.
 *
 * Two surfaces:
 *  - the read-only catalog (`list`/`Integration`) used by the Assisted config
 *    editor to recognise integration config keys (kept unchanged);
 *  - the per-tour-operator Integrations tab: combined list, CRUD, per-restech
 *    config schemas, webhook actions, and the import-source autocomplete.
 *
 * Interfaces here are hand-written (feature wrappers never consume the generated
 * `src/api/types.ts`). The composite `id` (`booking:{restech}:{slug|_default}` or
 * `fleet:{pk}`) is the row identity used for retrieve/update/delete.
 */
import { apiDelete, apiGet, apiPatch, apiPost, ConsoleApiError } from './client';

// ---------------------------------------------------------------------------
// Config schema warnings (advisory; returned on create/update, never blocking)
// ---------------------------------------------------------------------------

/** One field-level config-schema mismatch, returned in `config_warnings`. */
export interface SchemaValidationError {
  /** Dot-joined key path ("" at the document root). */
  path: string;
  /** Concrete reason (the JSON-Schema validator message). */
  message: string;
  /** Failing JSON-Schema keyword: type | enum | minimum | required | … */
  keyword: string;
  /** The constraint value (expected type, enum list, bound, …). */
  expected?: unknown;
}

/** A short, actionable fix hint derived from the failing keyword + constraint. */
export function validationFixHint(err: SchemaValidationError): string | undefined {
  const exp = err.expected;
  switch (err.keyword) {
    case 'type': {
      const t = Array.isArray(exp) ? exp.join(' or ') : String(exp);
      if (t.includes('integer') || t.includes('number'))
        return 'Enter a number without quotes (e.g. 30).';
      if (t.includes('boolean')) return 'Use true or false.';
      if (t.includes('array')) return 'Provide a list of values.';
      if (t.includes('object')) return 'Provide an object of key/value pairs.';
      if (t.includes('string')) return 'Enter text.';
      return `Expected type: ${t}.`;
    }
    case 'enum':
      return Array.isArray(exp)
        ? `Choose one of: ${exp.map((v) => JSON.stringify(v)).join(', ')}.`
        : undefined;
    case 'minimum':
      return `Must be ≥ ${String(exp)}.`;
    case 'exclusiveMinimum':
      return `Must be greater than ${String(exp)}.`;
    case 'maximum':
      return `Must be ≤ ${String(exp)}.`;
    case 'exclusiveMaximum':
      return `Must be less than ${String(exp)}.`;
    case 'minLength':
      return `Must be at least ${String(exp)} character(s).`;
    case 'maxLength':
      return `Must be at most ${String(exp)} character(s).`;
    case 'pattern':
      return `Must match the required format (${String(exp)}).`;
    case 'required':
      return 'This field is required.';
    case 'additionalProperties':
      return 'Unexpected key — remove it or check the spelling.';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Catalog (existing — do not change shape; AssistedConfigEditor depends on it)
// ---------------------------------------------------------------------------

/** How a restech can query bookings during sync (SYNC_BOOKINGS_QUERY). */
export interface BookingQueryCapabilities {
  /** Booking-start range granularity; null = unsupported. */
  start_range: 'date' | 'datetime' | null;
  /** Updated/created range granularity; null = unsupported. */
  updated_range: 'date' | 'datetime' | null;
  updated_semantics: 'updated' | 'created' | null;
  /** Both ranges may be combined in a single query. */
  combined: boolean;
  /** Explicit booking-ID fetch is supported. */
  by_ids: boolean;
  /** Sync can be launched from the UI background-task flow. */
  ui_launchable: boolean;
}

/** How a restech can query orders during sync (SYNC_ORDERS_QUERY). */
export interface OrderQueryCapabilities {
  /** Order updated-at window support; null = unsupported. */
  updated_range: 'datetime' | null;
  /** Order created-at window support; null = unsupported. */
  created_range: 'datetime' | null;
  /** Explicit order-ID fetch is supported. */
  by_ids: boolean;
  /** Order IDs can be derived from bookings filtered by products + travel dates. */
  by_product_travel_date: boolean;
  /** Orders already in the local DB can be refetched. */
  reconcile_existing: boolean;
  /** The reconcile set can be narrowed by travel-time groups (past/active/future). */
  reconcile_groups: boolean;
  /** Sync can be launched from the UI background-task flow. */
  ui_launchable: boolean;
}

/** Notification-subscription type support of a restech (OCTO notifications). */
export interface NotificationTypesCapability {
  /** Types the vendor accepts subscriptions for (BOOKING_UPDATE, …). */
  supported: string[];
  /** Types our import pipeline actually processes; a supported-but-not-
   *  implemented type can still be subscribed in log_only ("observe only"). */
  implemented: string[];
}

export interface Integration {
  id: string;
  name: string;
  supports: string[];
  /** Present only for integrations with the booking_sync feature. */
  booking_query?: BookingQueryCapabilities;
  /** Present only for integrations with the order_sync feature. */
  order_query?: OrderQueryCapabilities;
  /** The restech API supports managing notification subscriptions
   *  (see /notification_subscriptions/). Flat, like the webhook flags. */
  can_manage_notification_subscriptions?: boolean;
  notification_types?: NotificationTypesCapability;
}

export function list(): Promise<{ results: Integration[] }> {
  return apiGet<{ results: Integration[] }>('/integrations/');
}

// ---------------------------------------------------------------------------
// Capability-enriched catalog (booking systems) for the creation wizard
// ---------------------------------------------------------------------------

export type WebhookSetupMode = 'api' | 'email' | 'manual' | 'none';

export interface IntegrationCatalogEntry extends Integration {
  manageable: boolean;
  supports_webhooks: boolean;
  can_install_webhook: boolean;
  webhook_setup_mode: WebhookSetupMode;
}

export function listCatalog(): Promise<{ results: IntegrationCatalogEntry[] }> {
  return apiGet<{ results: IntegrationCatalogEntry[] }>('/integrations/');
}

export interface FleetSystem {
  id: string;
  name: string;
}

/** Vehicle-tracking systems. Only Quartix exists today; hardcoded until the
 *  backend exposes a catalog endpoint (keeps callers stable when it lands). */
export function listFleetSystems(): Promise<{ results: FleetSystem[] }> {
  return Promise.resolve({ results: [{ id: 'quartix', name: 'Quartix' }] });
}

// ---------------------------------------------------------------------------
// Per-restech config schemas (backend is the single source of truth)
// ---------------------------------------------------------------------------

/** A composed draft-2020-12 JSON Schema (with x-enum-labels / x-secret / etc.). */
export type IntegrationSchema = {
  properties?: Record<string, unknown>;
  required?: string[];
  [k: string]: unknown;
};

export function getSchema(restech: string): Promise<IntegrationSchema> {
  return apiGet<IntegrationSchema>(`/integrations/${encodeURIComponent(restech)}/schema/`);
}

export function getAllSchemas(): Promise<Record<string, IntegrationSchema>> {
  return apiGet<Record<string, IntegrationSchema>>('/integration_schemas/');
}

// ---------------------------------------------------------------------------
// Import sources (source_id autocomplete)
// ---------------------------------------------------------------------------

/** Create/update state per catalogue kind. A product sync writes these three
 *  and never users, so the key's other six flags are not reported here. */
export interface ImportSourceFlags {
  tours: { create: boolean; update: boolean };
  activities: { create: boolean; update: boolean };
  itineraries: { create: boolean; update: boolean };
}

export interface ImportSource {
  id: number;
  code: string;
  name: string;
  is_enabled: boolean;
  is_default: boolean;
  /** What this key may write. A missing permission does not fail an import —
   *  the entity is dropped and logged at debug — so this belongs in the picker. */
  allow_import: ImportSourceFlags;
  /** Flags this key lacks for the kinds the caller said it wants. Empty unless
   *  the caller stated an intent; "missing" is meaningless without one. */
  missing_flags: string[];
}

export interface CreateImportSourceInput {
  name: string;
  /** Catalogue kinds the key must be able to write. All three when omitted. */
  entities?: string[];
}

/** Mint an import key granting exactly what a run needs, and nothing else.
 *
 *  Narrower than the tenant's default key, which grants all twelve flags
 *  including the user ones a product sync never writes. Needs
 *  `console_restech_manage`. */
export function createImportSource(
  toId: number,
  body: CreateImportSourceInput,
): Promise<ImportSource> {
  return apiPost(`/tour_operators/${toId}/import_sources/create/`, body);
}

export function searchImportSources(
  toId: number,
  query: string,
  signal?: AbortSignal,
): Promise<ImportSource[]> {
  return apiGet<{ results: ImportSource[] }>(
    `/tour_operators/${toId}/import_sources/?search=${encodeURIComponent(query)}`,
    { signal },
  ).then((r) => r.results);
}

export interface TagCategory {
  id: number;
  code: string;
  name: string;
  is_inherited: boolean;
}

/** Tag categories for the TO (incl. franchise parent), for config autocompletes. */
export function searchTagCategories(
  toId: number,
  query: string,
  signal?: AbortSignal,
): Promise<TagCategory[]> {
  return apiGet<{ results: TagCategory[] }>(
    `/tour_operators/${toId}/tag_categories/?search=${encodeURIComponent(query)}`,
    { signal },
  ).then((r) => r.results);
}

// ---------------------------------------------------------------------------
// Combined integration rows
// ---------------------------------------------------------------------------

export type IntegrationKind = 'booking' | 'fleet';

export interface WebhookFlags {
  disable_webhooks?: boolean;
  disable_booking_webhooks?: boolean;
  disable_order_webhooks?: boolean;
  disable_departure_webhooks?: boolean;
  disable_product_webhooks?: boolean;
}

export interface WebhookLastReceived {
  booking: string | null;
  order: string | null;
  departure: string | null;
  product: string | null;
}

/** Fleet-system activity summary (fleet rows only) for the grid's Activity cell. */
export interface FleetActivity {
  last_pull_ts: string | null;
  vehicle_count: number;
  vehicles_assigned: number;
}

export interface IntegrationRow {
  id: string;
  kind: IntegrationKind;
  restech: string | null;
  fleet_mgmt: string | null;
  slug: string | null;
  is_default: boolean | null;
  display_name: string;
  enabled: boolean;
  source_id: string | null;
  webhook_flags: WebhookFlags | null;
  webhook_last_received: WebhookLastReceived | null;
  fleet_activity: FleetActivity | null;
  /** Whether every key the restech's config schema marks required has a value.
   *  `unknown` for fleet rows, which validate at the model instead. */
  config_status: 'ok' | 'incomplete' | 'unknown';
  /** Required config keys with no usable value; empty unless `incomplete`. */
  missing_keys: string[];
  supports_webhooks: boolean;
  can_install_webhook: boolean;
  webhook_setup_mode: WebhookSetupMode;
  /** The restech rejects inbound webhooks with no shared secret, so leaving
   *  `webhook_auth_token` unset silently drops every delivery. */
  webhook_auth_required: boolean;
  /** The row's restech supports API-managed notification subscriptions
   *  (booking rows only; flat, like the webhook capability fields above). */
  can_manage_notification_subscriptions?: boolean;
  notification_types?: NotificationTypesCapability;
  created_ts: string | null;
  created_by: number | null;
}

export interface WebhookStatus {
  supports_webhooks: boolean;
  can_install_webhook: boolean;
  webhook_setup_mode: WebhookSetupMode;
  installed_webhooks: unknown;
  webhook_last_received: WebhookLastReceived | null;
  stats: unknown; // PHASE 2: rolling counts
}

export interface IntegrationDetail extends IntegrationRow {
  /** Advisory schema mismatches from the last create/update (non-blocking). */
  config_warnings?: SchemaValidationError[];
  // booking
  raw_config?: Record<string, unknown>;
  schema_ref?: string;
  meta?: Record<string, unknown> | null;
  webhook_status?: WebhookStatus;
  // fleet
  settings?: Record<string, unknown>;
  sync_type?: number;
  keep_location_history?: boolean;
  unassign_if_not_in_api?: boolean;
  hidden_vehicle_statuses?: string[];
  route_unassign_events?: string[];
}

export function listRows(toId: number): Promise<{ results: IntegrationRow[] }> {
  return apiGet<{ results: IntegrationRow[] }>(`/tour_operators/${toId}/integrations/`);
}

export function retrieve(
  toId: number,
  integrationId: string,
  opts?: { checkRemote?: boolean; reveal?: boolean },
): Promise<IntegrationDetail> {
  const params = new URLSearchParams();
  if (opts?.checkRemote) params.set('check_remote', 'true');
  if (opts?.reveal) params.set('reveal', 'true');
  const q = params.toString() ? `?${params.toString()}` : '';
  return apiGet<IntegrationDetail>(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/${q}`,
  );
}

// ---------------------------------------------------------------------------
// Create / update / delete
// ---------------------------------------------------------------------------

export interface BookingCreateInput {
  kind: 'booking';
  restech: string;
  account_mode: 'single' | 'multi';
  slug?: string;
  display_name?: string;
  config: Record<string, unknown>;
}

export interface FleetCreateInput {
  kind: 'fleet';
  fleet_mgmt: 'quartix';
  enabled?: boolean;
  settings: Record<string, unknown>;
  keep_location_history?: boolean;
  unassign_if_not_in_api?: boolean;
  hidden_vehicle_statuses?: string[];
  route_unassign_events?: string[];
}

export type IntegrationCreateInput = BookingCreateInput | FleetCreateInput;

export function create(toId: number, body: IntegrationCreateInput): Promise<IntegrationRow> {
  return apiPost<IntegrationRow>(`/tour_operators/${toId}/integrations/`, body);
}

export interface IntegrationUpdateInput {
  // booking
  config?: Record<string, unknown>;
  display_name?: string;
  replace?: boolean;
  comment?: string;
  if_unmodified_since?: string;
  // fleet
  enabled?: boolean;
  settings?: Record<string, unknown>;
  keep_location_history?: boolean;
  unassign_if_not_in_api?: boolean;
  hidden_vehicle_statuses?: string[];
  route_unassign_events?: string[];
}

export function update(
  toId: number,
  integrationId: string,
  body: IntegrationUpdateInput,
): Promise<IntegrationDetail> {
  return apiPatch<IntegrationDetail>(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/`,
    body,
  );
}

export function remove(toId: number, integrationId: string): Promise<void> {
  return apiDelete<void>(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/`,
  );
}

// ---------------------------------------------------------------------------
// Webhook actions
// ---------------------------------------------------------------------------

export function installWebhook(
  toId: number,
  integrationId: string,
): Promise<{ installed: boolean; result: unknown }> {
  return apiPost(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/install_webhook/`,
  );
}

export function uninstallWebhook(
  toId: number,
  integrationId: string,
): Promise<{ uninstalled: number; kept_foreign?: number }> {
  return apiPost(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/uninstall_webhook/`,
  );
}

/** Why Send cannot send the webhook-setup e-mail to the vendor's support team: `disabled` — production with
 *  automatic e-mails to vendor support switched off (send it by hand); `sandbox` — a sandbox never e-mails
 *  vendor support; `error` — whether the operator is a sandbox could not be read. */
export type WebhookEmailSendDisabledReason = 'disabled' | 'sandbox' | 'error';

export interface WebhookEmailPreview {
  recipients: string[];
  reply_to: string[];
  subject: string;
  body: string;
  editable: boolean;
  /** Whether Send can send it. Absent from a backend older than the vendor-support gate, which sends. */
  send_enabled?: boolean;
  send_disabled_reason?: WebhookEmailSendDisabledReason | null;
}

/** What to send by hand: as it would have been sent, plus the body as plain text. */
export interface WebhookEmailManual {
  recipients: string[];
  reply_to: string[];
  subject: string;
  body: string;
  body_text: string;
}

/** The send's 409: not sent by rule, never a failure. `manual` only when switched off (a sandbox's is
 *  never to be sent by hand); `captured` only for a sandbox (recorded in its outbox). */
export interface WebhookEmailWithheld {
  sent: false;
  code: 'vendor_support_email_disabled' | 'vendor_support_email_sandbox';
  detail: string;
  recipients: string[];
  captured?: boolean;
  manual?: WebhookEmailManual;
}

/** The send's 409 body, or null for anything else (an error to show as one). */
export function webhookEmailWithheld(e: unknown): WebhookEmailWithheld | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  const body = e.body as Partial<WebhookEmailWithheld> | undefined;
  return body?.code === 'vendor_support_email_disabled' || body?.code === 'vendor_support_email_sandbox'
    ? (body as WebhookEmailWithheld)
    : null;
}

export function webhookEmailPreview(
  toId: number,
  integrationId: string,
): Promise<WebhookEmailPreview> {
  return apiGet<WebhookEmailPreview>(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/webhook_email_preview/`,
  );
}

/** 200 only once it went out. A 409 is not sent by rule ({@link webhookEmailWithheld}); a 502 is a failure. */
export function sendWebhookEmail(
  toId: number,
  integrationId: string,
  body: { recipients: string[]; subject: string; body: string; reply_to?: string[] },
): Promise<{ sent: boolean; recipients: string[] }> {
  return apiPost(
    `/tour_operators/${toId}/integrations/${encodeURIComponent(integrationId)}/send_webhook_email/`,
    body,
  );
}
